/**
 * Player combatant construction for the world loop (HEX-60).
 *
 * Single source of truth for the player's combat shape now that the V1
 * `Class` layout is retired (ADR-0015 has no classes). `makePlayerCombatant`
 * builds a symmetric `Combatant` from a persisted `Character`: HP carries across
 * combats and the loadout is the character's own persisted `inventory` (HEX-63).
 *
 * `makePlayerStarterInventory` is the §5/§19 genesis deck — Power Strike + Brace
 * + Reroll on the 3×3 loadout, the only card store (ADR-0022). It seeds new
 * characters (CharacterFlow) and is the migration default for pre-HEX-63 rows.
 * `createCombat` deep-clones the combatant, so passing the persisted inventory
 * straight through never lets combat mutate the stored deck.
 */

import type { Character } from '../core/character';
import { STARTER_ATTACK, STARTER_DEFENSE, STARTER_SUPPORT } from './cards';
import { createStatusBag } from './status';
import type { Combatant, GridPlacement, Inventory } from './types';

/**
 * Player max HP. Pinned here as the single source of truth — `ShopFlow`'s heal
 * cap and `CharacterFlow`'s spawn HP both read it. Replaces the pre-HEX-60
 * `warrior.baseHp` pin; the value is unchanged (20).
 */
export const PLAYER_MAX_HP = 20;

/** Player base dice pool before any equipped die cards (ADR-0015 §2). */
export const PLAYER_BASE_POOL_SIZE = 3;

function place(card: GridPlacement['card'], row: number, col: number): GridPlacement {
  return { card, origin: { row, col }, rotated: false };
}

/**
 * The starter inventory: §5 + §19 starter trio on the 3×3 loadout — the only
 * card store (ADR-0022). Power Strike 2×1 at (0,0), Brace 2×1 at (1,0), Reroll
 * 1×1 at (2,0) — three non-conflicting placements, five cells free.
 */
export function makePlayerStarterInventory(): Inventory {
  return {
    loadout: {
      placements: [
        place(STARTER_ATTACK, 0, 0),
        place(STARTER_DEFENSE, 1, 0),
        place(STARTER_SUPPORT, 2, 0),
      ],
      capacity: { rows: 3, cols: 3 },
    },
  };
}

/**
 * Build a fresh runtime `Combatant` for `character`. Current HP is carried from
 * the persisted value (HEX-34 cross-combat HP); `maxHp` is the fixed
 * `PLAYER_MAX_HP` cap. Each call gets its own status bag / trap / frozen maps so
 * concurrent combats never share mutable runtime.
 */
export function makePlayerCombatant(character: Character): Combatant {
  return {
    id: character.id,
    name: character.name,
    hp: character.hp,
    maxHp: PLAYER_MAX_HP,
    inventory: character.inventory,
    dicePool: [],
    // The free baseline: 3 implicit PLAIN_D6 (ADR-0024 §1). Specialist dice the
    // character has packed live as `die`-cards in `inventory.loadout` and roll on
    // top of this — the roll source is assembled by `poolDice` (cardEngine).
    basePoolSize: PLAYER_BASE_POOL_SIZE,
    statusBag: createStatusBag(),
    block: 0,
    trapState: new Map(),
    frozenCards: new Map(),
    oncePerCombatFired: new Set(),
    oncePerTurnFired: new Set(),
  };
}
